import Phaser from 'phaser'
import { COLORS, DIFFICULTY_CONFIG, Difficulty, PEARL_COLORS, PEARL_RADIUS, HEX_OFFSET } from '../config'

interface Pearl {
  sprite: Phaser.GameObjects.Sprite
  colorIndex: number
  row: number
  col: number
}

interface GridCell {
  x: number
  y: number
  pearl: Pearl | null
}

export class GameScene extends Phaser.Scene {
  // Grid
  private grid: GridCell[][] = []
  private gridWidth: number = 9
  private gridRows: number = 12
  private gridOffsetX: number = 0
  private gridOffsetY: number = 60
  private gridDescent: number = 0
  
  // Slingshot
  private slingshotX: number = 0
  private slingshotY: number = 0
  private currentPearl!: Phaser.GameObjects.Sprite
  private currentColorIndex: number = 0
  private nextColorIndex: number = 0
  private isDragging: boolean = false
  private dragStartPos: { x: number, y: number } = { x: 0, y: 0 }
  private trajectoryDots: Phaser.GameObjects.Sprite[] = []
  
  // Flying pearl
  private flyingPearl: Phaser.GameObjects.Sprite | null = null
  private flyingVelocity: { x: number, y: number } = { x: 0, y: 0 }
  private isFlying: boolean = false
  
  // Game state
  private score: number = 0
  private pearlsEarned: number = 0
  private isGameOver: boolean = false
  private difficulty!: Difficulty
  private numColors: number = 4
  
  // UI
  private scoreText!: Phaser.GameObjects.Text
  private pearlsText!: Phaser.GameObjects.Text
  private nextPearlPreview!: Phaser.GameObjects.Sprite
  private dangerLine!: Phaser.GameObjects.Graphics
  private dangerLineY: number = 0
  
  // Thea character
  private thea!: Phaser.GameObjects.Sprite
  
  // Debug
  private debugGraphics!: Phaser.GameObjects.Graphics
  private debugMode: boolean = false // Set to true to see collision zones

  constructor() {
    super({ key: 'GameScene' })
  }

  create() {
    const { width, height } = this.cameras.main
    
    // Get difficulty setting
    this.difficulty = this.registry.get('difficulty') || 'easy'
    const config = DIFFICULTY_CONFIG[this.difficulty]
    
    this.gridWidth = config.gridWidth
    this.numColors = config.pearlColors
    
    // Reset game state
    this.score = 0
    this.pearlsEarned = 0
    this.isGameOver = false
    this.gridDescent = 0
    this.isFlying = false
    this.flyingPearl = null
    this.grid = []
    
    // Calculate grid positioning
    const totalGridWidth = this.gridWidth * PEARL_RADIUS * 2
    this.gridOffsetX = (width - totalGridWidth) / 2 + PEARL_RADIUS
    this.gridOffsetY = 70
    
    // Danger line position
    this.dangerLineY = height - 150
    
    // Slingshot position
    this.slingshotX = width / 2
    this.slingshotY = height - 60
    
    // Create background
    this.createBackground()
    
    // Create danger line
    this.createDangerLine()
    
    // Create grid
    this.initializeGrid()
    this.populateStartingPearls(config.startingRows)
    
    // Create Thea on the side
    this.createThea()
    
    // Create slingshot and current pearl
    this.createSlingshot()
    
    // Create HUD
    this.createHUD()
    
    // Create trajectory dots
    this.createTrajectoryDots()
    
    // Input handlers
    this.input.on('pointerdown', this.onPointerDown, this)
    this.input.on('pointermove', this.onPointerMove, this)
    this.input.on('pointerup', this.onPointerUp, this)
    
    // Handle pointer leaving the game window while dragging
    this.input.on('gameout', this.onPointerLeave, this)
    
    // Also handle when pointer is released outside the game (backup)
    this.game.canvas.addEventListener('mouseleave', () => {
      if (this.isDragging && !this.isFlying) {
        this.onPointerUp()
      }
    })
    
    // Debug graphics (press D to toggle)
    this.debugGraphics = this.add.graphics()
    this.debugGraphics.setDepth(1000)
    this.input.keyboard?.on('keydown-D', () => {
      this.debugMode = !this.debugMode
      console.log(`[Pearl Pop] Debug mode: ${this.debugMode}`)
    })
  }
  
  onPointerLeave() {
    // If dragging when pointer leaves the game, shoot the pearl
    if (this.isDragging && !this.isFlying) {
      console.log('[Pearl Pop] Pointer left game - auto-shooting')
      this.onPointerUp()
    }
  }

  createBackground() {
    const { width, height } = this.cameras.main
    
    // Gradient background
    const bg = this.add.graphics()
    for (let y = 0; y < height; y += 2) {
      const ratio = y / height
      const r = Math.floor(30 + (40 - 30) * ratio)
      const g = Math.floor(58 + (90 - 58) * ratio)
      const b = Math.floor(95 + (120 - 95) * ratio)
      bg.fillStyle(Phaser.Display.Color.GetColor(r, g, b), 1)
      bg.fillRect(0, y, width, 2)
    }
    
    // Decorative coral at bottom
    for (let i = 0; i < 6; i++) {
      const coral = this.add.graphics()
      coral.fillStyle([0x2D5A5A, 0x3D6A6A, 0x4D7A7A][i % 3], 0.3)
      const x = i * 110 + Math.random() * 40
      const h = 25 + Math.random() * 40
      coral.fillRoundedRect(x, height - h, 30 + Math.random() * 30, h, 8)
    }
    
    // Game board area background
    const boardBg = this.add.graphics()
    boardBg.fillStyle(COLORS.paleAqua, 0.15)
    boardBg.fillRoundedRect(20, 50, width - 40, this.dangerLineY - 40, 15)
  }

  createDangerLine() {
    const { width } = this.cameras.main
    
    this.dangerLine = this.add.graphics()
    this.dangerLine.lineStyle(3, COLORS.deepPurple, 0.8)
    this.dangerLine.beginPath()
    this.dangerLine.moveTo(30, this.dangerLineY)
    this.dangerLine.lineTo(width - 30, this.dangerLineY)
    this.dangerLine.strokePath()
    
    // Warning text
    this.add.text(width / 2, this.dangerLineY + 15, '⚠️ DANGER LINE ⚠️', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '12px',
      color: '#6B4D94'
    }).setOrigin(0.5)
  }

  initializeGrid() {
    this.grid = []
    
    for (let row = 0; row < this.gridRows; row++) {
      this.grid[row] = []
      const isEvenRow = row % 2 === 0
      const cols = isEvenRow ? this.gridWidth : this.gridWidth - 1
      
      for (let col = 0; col < cols; col++) {
        const x = this.getGridX(row, col)
        const y = this.getGridY(row)
        
        this.grid[row][col] = {
          x,
          y,
          pearl: null
        }
      }
    }
  }

  getGridX(row: number, col: number): number {
    const isEvenRow = row % 2 === 0
    const offset = isEvenRow ? 0 : PEARL_RADIUS
    return this.gridOffsetX + col * PEARL_RADIUS * 2 + offset
  }

  getGridY(row: number): number {
    return this.gridOffsetY + row * HEX_OFFSET * 0.85 + this.gridDescent
  }

  populateStartingPearls(rows: number) {
    for (let row = 0; row < rows; row++) {
      const isEvenRow = row % 2 === 0
      const cols = isEvenRow ? this.gridWidth : this.gridWidth - 1
      
      for (let col = 0; col < cols; col++) {
        const colorIndex = Phaser.Math.Between(0, this.numColors - 1)
        this.createPearlAtCell(row, col, colorIndex)
      }
    }
  }

  createPearlAtCell(row: number, col: number, colorIndex: number): Pearl | null {
    if (!this.grid[row] || !this.grid[row][col]) return null
    
    const cell = this.grid[row][col]
    const color = PEARL_COLORS[colorIndex]
    
    // Position sprite at cell.y + gridDescent (current visual position)
    const sprite = this.add.sprite(cell.x, cell.y + this.gridDescent, `pearl_${color.name}`)
    sprite.setScale(1)
    
    const pearl: Pearl = {
      sprite,
      colorIndex,
      row,
      col
    }
    
    cell.pearl = pearl
    return pearl
  }

  createThea() {
    const { height } = this.cameras.main
    
    this.thea = this.add.sprite(50, height - 200, 'thea')
    this.thea.setScale(0.18)
    this.thea.setFlipX(true)
    
    // Gentle bobbing
    this.tweens.add({
      targets: this.thea,
      y: this.thea.y - 8,
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    })
  }

  createSlingshot() {
    // Slingshot visual base
    this.add.sprite(this.slingshotX, this.slingshotY + 25, 'slingshot').setScale(0.8)
    
    // Create the current pearl to shoot
    this.loadNextPearl()
  }

  // Get a list of colors that are currently in the grid (with counts)
  getColorsInGrid(): Map<number, number> {
    const colorCounts = new Map<number, number>()
    
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (cell?.pearl) {
          const colorIndex = cell.pearl.colorIndex
          colorCounts.set(colorIndex, (colorCounts.get(colorIndex) || 0) + 1)
        }
      }
    }
    
    return colorCounts
  }
  
  // Pick a color weighted towards colors that exist in the grid
  getWeightedRandomColor(): number {
    const colorCounts = this.getColorsInGrid()
    
    // If no colors in grid (shouldn't happen), return random
    if (colorCounts.size === 0) {
      return Phaser.Math.Between(0, this.numColors - 1)
    }
    
    // Build weighted array: colors in grid get high weight, others get small chance
    const weights: { colorIndex: number, weight: number }[] = []
    
    for (let i = 0; i < this.numColors; i++) {
      if (colorCounts.has(i)) {
        // Colors in grid: weight based on count (more pearls = more likely)
        // Minimum weight of 10 for any color in grid
        weights.push({ colorIndex: i, weight: Math.max(10, colorCounts.get(i)! * 5) })
      } else {
        // Colors NOT in grid: very small chance (5% of minimum grid color weight)
        weights.push({ colorIndex: i, weight: 0.5 })
      }
    }
    
    // Calculate total weight
    const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0)
    
    // Pick random value and find corresponding color
    let random = Math.random() * totalWeight
    
    for (const w of weights) {
      random -= w.weight
      if (random <= 0) {
        return w.colorIndex
      }
    }
    
    // Fallback (shouldn't reach here)
    return weights[0].colorIndex
  }
  
  loadNextPearl() {
    this.currentColorIndex = this.nextColorIndex || this.getWeightedRandomColor()
    this.nextColorIndex = this.getWeightedRandomColor()
    
    const color = PEARL_COLORS[this.currentColorIndex]
    
    if (this.currentPearl) {
      this.currentPearl.destroy()
    }
    
    this.currentPearl = this.add.sprite(this.slingshotX, this.slingshotY - 20, `pearl_${color.name}`)
    this.currentPearl.setScale(1.1)
    this.currentPearl.setDepth(10)
    
    // Update the next pearl preview in the HUD
    if (this.nextPearlPreview) {
      const nextColor = PEARL_COLORS[this.nextColorIndex]
      this.nextPearlPreview.setTexture(`pearl_${nextColor.name}`)
    }
    
    // Pulse effect
    this.tweens.add({
      targets: this.currentPearl,
      scale: { from: 1.1, to: 1.2 },
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    })
  }

  createHUD() {
    const { width } = this.cameras.main
    
    // Background panel
    const hudBg = this.add.graphics()
    hudBg.fillStyle(COLORS.darkNavy, 0.8)
    hudBg.fillRoundedRect(10, 10, width - 20, 45, 10)
    hudBg.setDepth(100)
    
    // Score
    this.add.text(80, 32, 'Score:', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '16px',
      color: '#98E8DC'
    }).setOrigin(0, 0.5).setDepth(100)
    
    this.scoreText = this.add.text(140, 32, '0', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '22px',
      color: '#FF9966',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5).setDepth(100)
    
    // Pearls earned
    this.add.text(width - 150, 32, '🐚', {
      fontSize: '20px'
    }).setOrigin(0, 0.5).setDepth(100)
    
    this.pearlsText = this.add.text(width - 120, 32, '+0', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '18px',
      color: '#5DD3C3',
      fontStyle: 'bold'
    }).setOrigin(0, 0.5).setDepth(100)
    
    // Next pearl preview
    this.add.text(width / 2 - 30, 32, 'Next:', {
      fontFamily: 'Arial, sans-serif',
      fontSize: '14px',
      color: '#98E8DC'
    }).setOrigin(0, 0.5).setDepth(100)

    const nextColor = PEARL_COLORS[this.nextColorIndex]
    this.nextPearlPreview = this.add.sprite(width / 2 + 25, 32, `pearl_${nextColor.name}`)
    this.nextPearlPreview.setScale(0.7).setDepth(100)
    
    // Pause button
    const pauseBtn = this.add.text(25, 32, '⏸️', {
      fontSize: '24px'
    }).setOrigin(0, 0.5).setDepth(100).setInteractive({ useHandCursor: true })
    
    pauseBtn.on('pointerdown', () => {
      // Could implement pause menu
    })
  }

  createTrajectoryDots() {
    this.trajectoryDots = []
    // More dots for longer trajectory preview (shows bounces)
    for (let i = 0; i < 40; i++) {
      const dot = this.add.sprite(0, 0, 'trajectoryDot')
      dot.setAlpha(0)
      dot.setScale(0.6)
      this.trajectoryDots.push(dot)
    }
  }

  onPointerDown(pointer: Phaser.Input.Pointer) {
    if (this.isGameOver || this.isFlying) return
    
    // Check if clicking near the current pearl
    const distance = Phaser.Math.Distance.Between(
      pointer.x, pointer.y,
      this.currentPearl.x, this.currentPearl.y
    )
    
    if (distance < 80) {
      this.isDragging = true
      this.dragStartPos = { x: this.slingshotX, y: this.slingshotY - 20 }
    }
  }

  onPointerMove(pointer: Phaser.Input.Pointer) {
    if (!this.isDragging || this.isFlying) return
    
    // Calculate pull back (inverted from drag direction)
    const maxPull = 100
    let dx = this.dragStartPos.x - pointer.x
    let dy = this.dragStartPos.y - pointer.y
    
    // Limit the pull distance
    const pullDistance = Math.sqrt(dx * dx + dy * dy)
    if (pullDistance > maxPull) {
      dx = (dx / pullDistance) * maxPull
      dy = (dy / pullDistance) * maxPull
    }
    
    // Position pearl at pulled back position (inverted)
    this.currentPearl.x = this.dragStartPos.x - dx
    this.currentPearl.y = this.dragStartPos.y - dy
    
    // Update trajectory preview
    this.updateTrajectory(dx, dy)
  }

  updateTrajectory(dx: number, dy: number) {
    const { width } = this.cameras.main
    const leftBound = PEARL_RADIUS + 30
    const rightBound = width - PEARL_RADIUS - 30
    const topBound = this.gridOffsetY + PEARL_RADIUS
    
    // Normalize direction and set constant speed
    const length = Math.sqrt(dx * dx + dy * dy)
    if (length < 10 || dy >= 0) {
      // Not enough pull or not aiming upward - hide trajectory
      this.trajectoryDots.forEach(dot => dot.setAlpha(0))
      return
    }
    
    // Constant speed for trajectory preview (match actual ball speed ratio)
    const speed = 16
    let velX = (dx / length) * speed
    let velY = (dy / length) * speed
    
    // Starting position
    let x = this.slingshotX
    let y = this.slingshotY - 20
    
    let hitPearl = false
    
    for (let i = 0; i < this.trajectoryDots.length; i++) {
      // Move forward
      x += velX
      y += velY
      
      // Wall bounce (left/right)
      if (x <= leftBound) {
        x = leftBound + (leftBound - x) // Reflect position
        velX = Math.abs(velX) // Bounce right
      } else if (x >= rightBound) {
        x = rightBound - (x - rightBound) // Reflect position
        velX = -Math.abs(velX) // Bounce left
      }
      
      // Ceiling bounce
      if (y <= topBound) {
        y = topBound + (topBound - y) // Reflect position
        velY = Math.abs(velY) // Bounce down
      }
      
      // Check if trajectory would hit a pearl
      if (!hitPearl) {
        for (let row = 0; row < this.grid.length; row++) {
          if (!this.grid[row]) continue
          for (let col = 0; col < this.grid[row].length; col++) {
            const cell = this.grid[row][col]
            if (!cell || !cell.pearl || !cell.pearl.sprite || !cell.pearl.sprite.active) continue
            
            const dist = Phaser.Math.Distance.Between(x, y, cell.pearl.sprite.x, cell.pearl.sprite.y)
            if (dist < PEARL_RADIUS * 2 - 2) { // Match actual collision radius
              hitPearl = true
              // Show this dot at the collision point
              this.trajectoryDots[i].x = x
              this.trajectoryDots[i].y = y
              this.trajectoryDots[i].setAlpha(1) // Highlight collision point
              this.trajectoryDots[i].setVisible(true)
              // Hide remaining dots
              for (let j = i + 1; j < this.trajectoryDots.length; j++) {
                this.trajectoryDots[j].setAlpha(0)
              }
              return
            }
          }
        }
      }
      
      this.trajectoryDots[i].x = x
      this.trajectoryDots[i].y = y
      this.trajectoryDots[i].setAlpha(0.8 - i * 0.02)
      this.trajectoryDots[i].setVisible(true)
      
      // Stop trajectory if it would go below slingshot (bounced back down)
      if (y > this.slingshotY) {
        // Hide remaining dots
        for (let j = i + 1; j < this.trajectoryDots.length; j++) {
          this.trajectoryDots[j].setAlpha(0)
        }
        break
      }
    }
  }

  onPointerUp() {
    if (!this.isDragging || this.isFlying) return
    this.isDragging = false
    
    // Calculate launch velocity
    const dx = this.dragStartPos.x - this.currentPearl.x
    const dy = this.dragStartPos.y - this.currentPearl.y
    
    // Hide trajectory
    this.trajectoryDots.forEach(dot => dot.setAlpha(0))
    
    // Only shoot if pulled back enough (pulled DOWN to shoot UP)
    // When dragging down, dy becomes negative, so check dy < -20
    if (dy < -20) {
      this.shootPearl(dx, dy)
    } else {
      // Reset pearl position
      this.currentPearl.x = this.slingshotX
      this.currentPearl.y = this.slingshotY - 20
    }
  }

  shootPearl(dx: number, dy: number) {
    this.isFlying = true

    // Normalize direction for constant speed
    const length = Math.sqrt(dx * dx + dy * dy)
    const speed = 8 // Constant speed (pixels per frame at 60fps)
    
    this.flyingVelocity = {
      x: (dx / length) * speed,
      y: (dy / length) * speed
    }

    // The current pearl becomes the flying pearl
    this.flyingPearl = this.currentPearl
    this.flyingPearl.setDepth(5)

    // Stop the pulse tween
    this.tweens.killTweensOf(this.flyingPearl)
    this.flyingPearl.setScale(1)
  }

  update(_time: number, delta: number) {
    if (this.isGameOver) return
    
    // Clean up any invalid pearl references in the grid
    this.validateGrid()
    
    // Handle flying pearl
    if (this.isFlying && this.flyingPearl) {
      this.updateFlyingPearl(delta)
    }
    
    // Grid descent (for medium and hard)
    const config = DIFFICULTY_CONFIG[this.difficulty]
    if (config.descentSpeed > 0) {
      this.gridDescent += config.descentSpeed * delta / 16
      this.updateGridPositions()
      this.checkDangerLine()
    }
    
    // Debug visualization
    this.drawDebug()
  }
  
  drawDebug() {
    this.debugGraphics.clear()
    
    if (!this.debugMode) return
    
    // Draw collision zones for all pearls in grid
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (!cell || !cell.pearl) continue
        
        const sprite = cell.pearl.sprite
        if (!sprite || !sprite.active) continue
        
        // Draw collision circle (red = where collision is detected)
        this.debugGraphics.lineStyle(2, 0xFF0000, 0.8)
        this.debugGraphics.strokeCircle(sprite.x, sprite.y, PEARL_RADIUS * 2 - 2)
        
        // Draw sprite center (green dot)
        this.debugGraphics.fillStyle(0x00FF00, 1)
        this.debugGraphics.fillCircle(sprite.x, sprite.y, 3)
        
        // Draw cell position (blue = where grid thinks pearl is)
        this.debugGraphics.lineStyle(1, 0x0000FF, 0.5)
        this.debugGraphics.strokeCircle(cell.x, cell.y + this.gridDescent, PEARL_RADIUS)
      }
    }
    
    // Draw flying pearl collision zone
    if (this.flyingPearl && this.isFlying) {
      this.debugGraphics.lineStyle(2, 0xFFFF00, 1)
      this.debugGraphics.strokeCircle(this.flyingPearl.x, this.flyingPearl.y, PEARL_RADIUS)
    }
  }
  
  // Clean up any invalid pearl references (destroyed sprites, etc.)
  validateGrid() {
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (!cell || !cell.pearl) continue
        
        const sprite = cell.pearl.sprite
        // Remove pearl reference if sprite is destroyed, not active, or fading out
        if (!sprite || !sprite.active || sprite.alpha < 0.5) {
          cell.pearl = null
        }
      }
    }
  }

  updateFlyingPearl(delta: number) {
    if (!this.flyingPearl) return

    // Cap delta to prevent huge jumps if frame rate drops
    const cappedDelta = Math.min(delta, 32)
    const dt = cappedDelta / 16 // Normalize to ~60fps

    const { width } = this.cameras.main
    const leftBound = PEARL_RADIUS + 30
    const rightBound = width - PEARL_RADIUS - 30
    const topBound = this.gridOffsetY + PEARL_RADIUS
    
    // Update position
    this.flyingPearl.x += this.flyingVelocity.x * dt
    this.flyingPearl.y += this.flyingVelocity.y * dt

    // Wall bouncing (left/right) with proper reflection
    if (this.flyingPearl.x <= leftBound) {
      this.flyingPearl.x = leftBound + (leftBound - this.flyingPearl.x) // Reflect position
      this.flyingVelocity.x = Math.abs(this.flyingVelocity.x) // Bounce right
    } else if (this.flyingPearl.x >= rightBound) {
      this.flyingPearl.x = rightBound - (this.flyingPearl.x - rightBound) // Reflect position
      this.flyingVelocity.x = -Math.abs(this.flyingVelocity.x) // Bounce left
    }

    // Ceiling bouncing with proper reflection
    if (this.flyingPearl.y <= topBound) {
      this.flyingPearl.y = topBound + (topBound - this.flyingPearl.y) // Reflect position
      this.flyingVelocity.y = Math.abs(this.flyingVelocity.y) // Bounce down
    }
    
    // If ball goes below the slingshot after bouncing, it's lost - respawn
    if (this.flyingPearl.y > this.slingshotY + 100) {
      console.log('[Pearl Pop] Ball went off bottom - respawning')
      this.flyingPearl.destroy()
      this.flyingPearl = null
      this.isFlying = false
      this.loadNextPearl()
      return
    }
    
    // Check for collision with grid pearls
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (!cell || !cell.pearl) continue
        
        const pearl = cell.pearl
        const sprite = pearl.sprite
        
        // Skip if sprite is destroyed, invisible, or being animated out
        if (!sprite || !sprite.active || sprite.alpha < 0.5) {
          // Clean up invalid pearl reference
          cell.pearl = null
          continue
        }
        
        // Use the SPRITE position for collision (what the player sees)
        const dist = Phaser.Math.Distance.Between(
          this.flyingPearl.x, this.flyingPearl.y,
          sprite.x, sprite.y
        )
        
        if (dist < PEARL_RADIUS * 2 - 2) {
          console.log(`[Pearl Pop] COLLISION at (${row},${col}): dist=${dist.toFixed(1)}, flying=(${this.flyingPearl.x.toFixed(1)},${this.flyingPearl.y.toFixed(1)}), sprite=(${sprite.x.toFixed(1)},${sprite.y.toFixed(1)}), vel=(${this.flyingVelocity.x.toFixed(2)},${this.flyingVelocity.y.toFixed(2)})`)
          // Pass the hit pearl's location so we snap adjacent to it
          this.snapPearlToGrid({ row, col })
          return
        }
      }
    }
  }

  snapPearlToGrid(hitPearl: { row: number, col: number } | null) {
    if (!this.flyingPearl) return
    
    const flyingX = this.flyingPearl.x
    const flyingY = this.flyingPearl.y
    
    let bestCell: { row: number, col: number, dist: number } | null = null
    
    // Maximum reasonable snap distance (shouldn't be snapping to cells far from collision point)
    const maxSnapDistance = PEARL_RADIUS * 4
    
    // If we hit a specific pearl, prioritize empty cells adjacent to THAT pearl
    if (hitPearl) {
      const neighbors = this.getHexNeighbors(hitPearl.row, hitPearl.col)
      
      for (const n of neighbors) {
        const cell = this.grid[n.row]?.[n.col]
        if (!cell) continue
        
        // Also validate this cell doesn't have a ghost pearl
        if (cell.pearl) {
          if (!cell.pearl.sprite || !cell.pearl.sprite.active) {
            cell.pearl = null // Clean up ghost
          } else {
            continue // Skip - cell has valid pearl
          }
        }
        
        const cellY = cell.y + this.gridDescent
        const dist = Phaser.Math.Distance.Between(flyingX, flyingY, cell.x, cellY)
        
        // Only consider cells within reasonable distance
        if (dist > maxSnapDistance) continue
        
        if (!bestCell || dist < bestCell.dist) {
          bestCell = { row: n.row, col: n.col, dist }
        }
      }
    }
    
    // If no cell found adjacent to hit pearl (or hit ceiling), find nearest valid cell
    if (!bestCell) {
      for (let row = 0; row < this.grid.length; row++) {
        if (!this.grid[row]) continue
        
        for (let col = 0; col < this.grid[row].length; col++) {
          const cell = this.grid[row][col]
          if (!cell) continue
          
          // Clean up ghost pearls
          if (cell.pearl) {
            if (!cell.pearl.sprite || !cell.pearl.sprite.active) {
              cell.pearl = null
            } else {
              continue // Skip - cell has valid pearl
            }
          }
          
          // Check if this cell is valid (adjacent to existing pearl or in top row)
          const isTopRow = row === 0
          const hasAdjacentPearl = this.hasAdjacentPearl(row, col)
          
          if (!isTopRow && !hasAdjacentPearl) continue // Skip isolated cells
          
          const cellY = cell.y + this.gridDescent
          const dist = Phaser.Math.Distance.Between(flyingX, flyingY, cell.x, cellY)
          
          if (!bestCell || dist < bestCell.dist) {
            bestCell = { row, col, dist }
          }
        }
      }
    }
    
    console.log(`[Pearl Pop] Snapping to cell:`, bestCell, hitPearl ? `(hit pearl at ${hitPearl.row},${hitPearl.col})` : '(ceiling hit)')
    
    if (bestCell) {
      const cell = this.grid[bestCell.row][bestCell.col]
      
      // Create pearl at cell
      const pearl: Pearl = {
        sprite: this.flyingPearl,
        colorIndex: this.currentColorIndex,
        row: bestCell.row,
        col: bestCell.col
      }
      
      cell.pearl = pearl
      
      // Animate snap
      this.tweens.add({
        targets: this.flyingPearl,
        x: cell.x,
        y: cell.y + this.gridDescent,
        duration: 50,
        ease: 'Quad.easeOut',
        onComplete: () => {
          this.checkMatches(bestCell!.row, bestCell!.col)
        }
      })
    } else {
      // Truly no valid cell - place in top row
      console.log('[Pearl Pop] No valid cell found, placing in top row')
      for (let col = 0; col < this.grid[0].length; col++) {
        if (!this.grid[0][col].pearl) {
          const cell = this.grid[0][col]
          const pearl: Pearl = {
            sprite: this.flyingPearl,
            colorIndex: this.currentColorIndex,
            row: 0,
            col
          }
          cell.pearl = pearl
          
          this.tweens.add({
            targets: this.flyingPearl,
            x: cell.x,
            y: cell.y + this.gridDescent,
            duration: 50,
            ease: 'Quad.easeOut',
            onComplete: () => {
              this.checkMatches(0, col)
            }
          })
          break
        }
      }
    }
    
    this.flyingPearl = null
    this.isFlying = false
    
    // Load next pearl after a short delay
    this.time.delayedCall(100, () => {
      if (!this.isGameOver) {
        this.loadNextPearl()
        this.checkDangerLine()
      }
    })
  }

  // Check if a cell has any adjacent pearl (used for valid snap detection)
  hasAdjacentPearl(row: number, col: number): boolean {
    const neighbors = this.getHexNeighbors(row, col)
    return neighbors.some(n => {
      const cell = this.grid[n.row]?.[n.col]
      return cell && cell.pearl !== null
    })
  }

  checkMatches(row: number, col: number) {
    const cell = this.grid[row][col]
    if (!cell || !cell.pearl) return
    
    const colorIndex = cell.pearl.colorIndex
    console.log(`[Pearl Pop] Checking matches from (${row}, ${col}) color=${colorIndex}`)
    
    const matches = this.findConnectedPearls(row, col, colorIndex)
    
    console.log(`[Pearl Pop] Found ${matches.length} matches:`, matches)
    
    if (matches.length >= 2) {
      // Pop the matched pearls (only need 2+ to match now!)
      this.popPearls(matches)
      
      // Check for orphans after pop animation completes
      this.time.delayedCall(300, () => {
        this.checkOrphans()
      })
    } else {
      // Even if no match, still check for orphans (in case grid state changed)
      this.time.delayedCall(100, () => {
        this.checkOrphans()
      })
    }
  }

  findConnectedPearls(startRow: number, startCol: number, colorIndex: number): { row: number, col: number }[] {
    const visited = new Set<string>()
    const matches: { row: number, col: number }[] = []
    
    const dfs = (row: number, col: number) => {
      const key = `${row},${col}`
      if (visited.has(key)) return
      
      // Check if row exists
      if (row < 0 || row >= this.grid.length || !this.grid[row]) return
      
      // Check if col exists in this row
      if (col < 0 || col >= this.grid[row].length) return
      
      const cell = this.grid[row][col]
      if (!cell) return
      
      if (!cell.pearl) {
        return
      }
      
      if (cell.pearl.colorIndex !== colorIndex) {
        return
      }
      
      visited.add(key)
      matches.push({ row, col })
      
      // Get hex neighbors and recursively check
      const neighbors = this.getHexNeighbors(row, col)
      const neighborInfo = neighbors.map(n => {
        const nCell = this.grid[n.row]?.[n.col]
        const nColor = nCell?.pearl?.colorIndex
        return `(${n.row},${n.col}):color=${nColor ?? 'empty'}`
      }).join(', ')
      console.log(`[Pearl Pop] Cell (${row},${col}) color=${colorIndex} neighbors: [${neighborInfo}]`)
      
      for (const n of neighbors) {
        dfs(n.row, n.col)
      }
    }
    
    dfs(startRow, startCol)
    return matches
  }

  getHexNeighbors(row: number, col: number): { row: number, col: number }[] {
    const isEvenRow = row % 2 === 0
    const neighbors: { row: number, col: number }[] = []
    
    // Horizontal neighbors
    neighbors.push({ row, col: col - 1 })
    neighbors.push({ row, col: col + 1 })
    
    // Diagonal neighbors depend on even/odd row
    if (isEvenRow) {
      neighbors.push({ row: row - 1, col: col - 1 })
      neighbors.push({ row: row - 1, col })
      neighbors.push({ row: row + 1, col: col - 1 })
      neighbors.push({ row: row + 1, col })
    } else {
      neighbors.push({ row: row - 1, col })
      neighbors.push({ row: row - 1, col: col + 1 })
      neighbors.push({ row: row + 1, col })
      neighbors.push({ row: row + 1, col: col + 1 })
    }
    
    // Filter valid neighbors
    return neighbors.filter(n => {
      return n.row >= 0 && n.row < this.grid.length &&
             n.col >= 0 && this.grid[n.row] && n.col < this.grid[n.row].length
    })
  }

  popPearls(matches: { row: number, col: number }[]) {
    // Calculate score
    const baseScore = matches.length * 10
    const bonus = matches.length > 3 ? (matches.length - 3) * 15 : 0
    this.score += baseScore + bonus
    
    // Calculate pearls earned
    const pearlsFromMatch = matches.length >= 5 ? 8 : matches.length >= 4 ? 5 : 3
    this.pearlsEarned += pearlsFromMatch
    
    // Update UI
    this.scoreText.setText(this.score.toString())
    this.pearlsText.setText(`+${this.pearlsEarned}`)
    
    // Pop animation for each pearl
    matches.forEach(({ row, col }, index) => {
      const cell = this.grid[row][col]
      if (!cell || !cell.pearl) return
      
      const pearl = cell.pearl
      
      // Pop animation with delay
      this.time.delayedCall(index * 30, () => {
        this.tweens.add({
          targets: pearl.sprite,
          scale: 1.4,
          alpha: 0,
          duration: 200,
          ease: 'Quad.easeOut',
          onComplete: () => {
            pearl.sprite.destroy()
          }
        })
        
        // Spawn sparkle particles
        this.spawnSparkles(cell.x, cell.y + this.gridDescent, pearl.colorIndex)
      })
      
      cell.pearl = null
    })
    
    // Screen effects for big combos
    if (matches.length >= 5) {
      this.cameras.main.shake(150, 0.01)
      
      // Thea celebrates!
      this.tweens.add({
        targets: this.thea,
        angle: { from: -10, to: 10 },
        duration: 150,
        yoyo: true,
        repeat: 2
      })
    }
  }

  spawnSparkles(x: number, y: number, colorIndex: number) {
    const color = PEARL_COLORS[colorIndex]
    
    for (let i = 0; i < 6; i++) {
      const sparkle = this.add.sprite(x, y, 'sparkle')
      sparkle.setTint(color.hex)
      sparkle.setScale(0.5)
      
      const angle = (i / 6) * Math.PI * 2
      const distance = 30 + Math.random() * 20
      
      this.tweens.add({
        targets: sparkle,
        x: x + Math.cos(angle) * distance,
        y: y + Math.sin(angle) * distance,
        alpha: 0,
        scale: 0,
        duration: 300,
        ease: 'Quad.easeOut',
        onComplete: () => sparkle.destroy()
      })
    }
  }

  checkOrphans() {
    console.log('[Pearl Pop] Checking for orphans...')
    
    // Find all pearls connected to the top row
    const connected = new Set<string>()
    
    // Start DFS from all pearls in first row
    for (let col = 0; col < this.grid[0].length; col++) {
      if (this.grid[0][col]?.pearl) {
        this.markConnected(0, col, connected)
      }
    }
    
    console.log(`[Pearl Pop] Connected pearls: ${connected.size}`, Array.from(connected))
    
    // Find orphans (pearls not connected)
    const orphans: { row: number, col: number, pearl: Pearl }[] = []
    
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (!cell || !cell.pearl) continue
        
        const key = `${row},${col}`
        if (!connected.has(key)) {
          console.log(`[Pearl Pop] Found orphan at (${row}, ${col})`)
          orphans.push({ row, col, pearl: cell.pearl })
          cell.pearl = null
        }
      }
    }
    
    console.log(`[Pearl Pop] Total orphans found: ${orphans.length}`)
    
    // Drop orphans
    if (orphans.length > 0) {
      this.dropOrphans(orphans)
    } else {
      // Check if grid is empty (win condition)
      this.checkWinCondition()
    }
  }

  markConnected(row: number, col: number, connected: Set<string>) {
    const key = `${row},${col}`
    if (connected.has(key)) return
    
    if (!this.grid[row] || !this.grid[row][col]) return
    if (!this.grid[row][col].pearl) return
    
    connected.add(key)
    
    const neighbors = this.getHexNeighbors(row, col)
    neighbors.forEach(n => this.markConnected(n.row, n.col, connected))
  }

  dropOrphans(orphans: { row: number, col: number, pearl: Pearl }[]) {
    const { height } = this.cameras.main
    
    // Add score and pearls for each orphan
    const orphanScore = orphans.length * 10
    const orphanPearls = orphans.length
    this.score += orphanScore
    this.pearlsEarned += orphanPearls
    
    this.scoreText.setText(this.score.toString())
    this.pearlsText.setText(`+${this.pearlsEarned}`)
    
    // Animate each orphan falling
    orphans.forEach(({ pearl }, index) => {
      this.time.delayedCall(index * 50, () => {
        this.tweens.add({
          targets: pearl.sprite,
          y: height + 50,
          angle: Phaser.Math.Between(-180, 180),
          duration: 800,
          ease: 'Quad.easeIn',
          onComplete: () => {
            pearl.sprite.destroy()
          }
        })
      })
    })
    
    // Check win after orphans fall
    this.time.delayedCall(orphans.length * 50 + 400, () => {
      this.checkWinCondition()
    })
  }

  updateGridPositions() {
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (cell && cell.pearl) {
          cell.pearl.sprite.y = cell.y + this.gridDescent
        }
      }
    }
  }

  checkDangerLine() {
    // Check if any pearl has crossed the danger line
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        const cell = this.grid[row][col]
        if (cell && cell.pearl) {
          if (cell.y + this.gridDescent > this.dangerLineY - PEARL_RADIUS) {
            this.gameOver(false)
            return
          }
        }
      }
    }
  }

  checkWinCondition() {
    // Check if all pearls are cleared
    let hasPearls = false
    
    for (let row = 0; row < this.grid.length; row++) {
      if (!this.grid[row]) continue
      
      for (let col = 0; col < this.grid[row].length; col++) {
        if (this.grid[row][col]?.pearl) {
          hasPearls = true
          break
        }
      }
      if (hasPearls) break
    }
    
    if (!hasPearls) {
      this.gameOver(true)
    }
  }

  gameOver(isWin: boolean) {
    if (this.isGameOver) return
    this.isGameOver = true
    
    // Update high score
    const highScore = this.registry.get('highScore') || 0
    const isNewHighScore = this.score > highScore
    if (isNewHighScore) {
      this.registry.set('highScore', this.score)
    }
    
    // Call the onGameOver callback
    const onGameOver = this.registry.get('onGameOver')
    if (onGameOver) {
      onGameOver(this.score, this.pearlsEarned)
    }
    
    // Transition to game over scene
    this.time.delayedCall(500, () => {
      this.scene.start('GameOverScene', {
        score: this.score,
        pearlsEarned: this.pearlsEarned,
        isWin,
        isNewHighScore,
        difficulty: this.difficulty
      })
    })
    
    // Visual feedback
    if (isWin) {
      this.cameras.main.flash(300, 93, 211, 195)
    } else {
      this.cameras.main.shake(300, 0.02)
      this.cameras.main.flash(200, 255, 100, 100)
    }
  }
}
